import type { DomainEvent, DomainEventKind, DomainEventOf } from './types';

export type DomainListener<K extends DomainEventKind> = (event: DomainEventOf<K>) => void;
export type AnyDomainListener = (event: DomainEvent) => void;

export class DomainEventEmitter {
  readonly #listeners = new Map<DomainEventKind, Set<AnyDomainListener>>();
  readonly #wildcard = new Set<AnyDomainListener>();

  on<K extends DomainEventKind>(kind: K, listener: DomainListener<K>): () => void {
    const set = this.#listeners.get(kind) ?? new Set<AnyDomainListener>();
    set.add(listener as AnyDomainListener);
    this.#listeners.set(kind, set);
    return () => {
      set.delete(listener as AnyDomainListener);
    };
  }

  onAny(listener: AnyDomainListener): () => void {
    this.#wildcard.add(listener);
    return () => {
      this.#wildcard.delete(listener);
    };
  }

  emit(event: DomainEvent): void {
    const set = this.#listeners.get(event.kind);
    if (set) {
      for (const listener of set) listener(event);
    }
    for (const listener of this.#wildcard) listener(event);
  }

  removeAll(): void {
    this.#listeners.clear();
    this.#wildcard.clear();
  }
}
